import { useAtomValue } from 'jotai';
import { Database, ExternalLink, Loader2, RotateCw } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { LayerData } from '@/atoms/layers.atom';
import { userAtom } from '@/atoms/user.atom';
import { formatDateTime } from '@/components/BatchList';
import { JobParamsPanel } from '@/components/JobParamsPanel';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getPlaceholderAvatar } from '@/core/avatar';
import { ProcessJobStatus } from '@/graphql/graphql';
import type { RerunChoice, RerunChoiceKey } from '@/lib/analyses/comparisonRerunChoices';
import { landxmlMergeWarningTexts, type LandxmlMergeStatsWarnings } from '@/lib/analyses/landxmlMergeWarnings';
import { isComparison, isDeleteWarned } from '@/lib/analyses/mergeRuns';
import { getAnalysis } from '@/lib/analyses/registry';
import {
  formatFileSize,
  runDurationMs,
  runElapsedMs,
  toDate,
  type AnalysisRunFileItem,
  type RunFiles,
  type RunRow,
} from '@/lib/analyses/runList';
import { isRunActive } from '@/lib/analyses/runs';
import { translateKey } from '@/lib/analyses/translateKey';
import { useAnalysisRunDetails, useAnalysisRunParams } from '@/lib/analyses/useAnalysisRunDetails';
import { useDeleteAnalysisRun } from '@/lib/analyses/useDeleteAnalysisRun';
import { isInternalUser } from '@/lib/email';
import { cn } from '@/lib/utils';

import { useFormatDuration } from './AnalysisRunRow';
import { ComparisonRunDetails } from './ComparisonRunDetails';
import {
  DeleteRunButton,
  ErrorBox,
  Field,
  FileList,
  InputList,
  muted,
  PARAMS_COLLAPSE_AFTER,
  Section,
  StepLine,
  useDrawerClock,
} from './runDetailsParts';
import { runPartsOf, usePartLabel } from './RunPartTags';
import { RunStatusIcon } from './RunStatusIcon';

export { PARAMS_COLLAPSE_AFTER };

interface Props {
  projectId: string;
  href: string;
  /** `?run=` — the drawer is open whenever this is set. */
  runId: string | null;
  /** The run behind `runId`; null when it is not (or no longer) listed. */
  row: RunRow | null;
  /** The list's clock — decides whether the run counts as active. */
  nowMs: number;
  /** The row's files (a comparison's: every part's, a shared report once). */
  files: RunFiles;
  /** Files per run id — which part of a comparison a file belongs to. */
  filesByRun: ReadonlyMap<string, RunFiles> | undefined;
  layersById: ReadonlyMap<string, LayerData>;
  canRerun: boolean;
  onClose: () => void;
  onRerun: () => void;
  /** A paired comparison's re-run choices (the footer's Re-run is a menu). */
  rerunChoices: readonly RerunChoice[];
  onRerunChoice: (key: RerunChoiceKey) => void;
  onOpenLayer: (layerId: string) => void;
  onDownload: (file: AnalysisRunFileItem) => void;
}

export type DrawerBodyProps = Omit<Props, 'runId' | 'row'> & { row: RunRow };

/**
 * The Analyses page's run-details drawer (spec 2026-10-10 run details): the
 * batch facts the data centre shows, for one run, without leaving the list. A
 * paired comparison (one hub row, two runs) gets `ComparisonRunDetails`,
 * which lists both results.
 *
 * Data: the slim `AnalysisRunDetails` (2 s poll only while the run is active)
 * and the root job's `analysisRunInput` (once, never polled). Files come from
 * the page's own `analysisRunFiles`.
 */
export function AnalysisRunDetailsDrawer({ runId, row, ...props }: Props) {
  const open = runId !== null;
  const drawerRef = useRef<HTMLDivElement>(null);
  return (
    <Sheet open={open} onOpenChange={(next) => !next && props.onClose()}>
      <SheetContent
        ref={drawerRef}
        data-testid='analysis-run-details'
        side='right'
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          // Keep focus inside the drawer without opening the Started tooltip.
          drawerRef.current?.focus({ preventScroll: true });
        }}
        className='w-full gap-0 p-0 outline-none sm:max-w-lg'>
        {!row ? (
          <NotFound />
        ) : isComparison(row.run) ? (
          <ComparisonRunDetails key={row.run.id} row={row} {...props} />
        ) : (
          <DrawerBody key={row.run.id} row={row} {...props} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function NotFound() {
  const { t } = useTranslation('project');
  return (
    <>
      <SheetHeader className='border-b border-slate-200/70 pr-10 dark:border-stone-800'>
        <SheetTitle>{t('analyses.details.title')}</SheetTitle>
      </SheetHeader>
      <SheetDescription data-testid='analysis-run-details-not-found' className={cn(muted, 'p-4')}>
        {t('analyses.details.notFound')}
      </SheetDescription>
    </>
  );
}

function DrawerBody({
  projectId,
  href,
  row,
  nowMs,
  files,
  layersById,
  canRerun,
  onClose,
  onRerun,
  onOpenLayer,
  onDownload,
}: DrawerBodyProps) {
  const { t, i18n } = useTranslation('project');
  const { t: tComponents } = useTranslation('components');
  const formatDuration = useFormatDuration();
  const partLabel = usePartLabel();
  const user = useAtomValue(userAtom);
  const { run, id, title } = row;
  const Icon = getAnalysis(id).icon;
  const isStaff = isInternalUser(user?.email);
  // A single Compare surfaces run names its result, like the hub row's tag.
  const [part] = runPartsOf(run);

  const active = isRunActive(run, nowMs);
  // The list's clock ticks every 15–60 s; the drawer's own adds 1 s steps
  // while the run is active, for a live duration.
  const drawerNow = useDrawerClock(active);
  const now = Math.max(drawerNow, nowMs);
  const { data: batch } = useAnalysisRunDetails(run, true);
  const params = useAnalysisRunParams(run.id, true);

  const { canDelete, isDeleting, confirmDelete } = useDeleteAnalysisRun(projectId, onClose);

  const absolute = (value: unknown) =>
    toDate(value).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const relative = (value: unknown) =>
    formatDateTime(toDate(value), (k, o) => translateKey(tComponents, k, o), now);

  const tookMs = active ? runElapsedMs(run, now) : runDurationMs(run);
  const resultLayer = run.resultLayerId ? layersById.get(run.resultLayerId) : undefined;
  const resultMetadata = resultLayer?.metadata as { landxmlMergeStats?: LandxmlMergeStatsWarnings } | null;
  const mergeWarnings = id === 'landxmlMerge'
    ? landxmlMergeWarningTexts(resultMetadata?.landxmlMergeStats, t) : [];
  const allFiles = [...files.pdfs, ...files.others];
  const steps = batch?.jobs ?? [];

  return (
    <>
      <SheetHeader className='border-b border-slate-200/70 pr-10 dark:border-stone-800'>
        <div className='flex items-center gap-2 text-xs text-slate-500 dark:text-stone-400'>
          <Icon className='size-4 shrink-0' aria-hidden />
          <span>{part ? `${title} · ${partLabel(part.part)}` : title}</span>
        </div>
        <SheetTitle data-testid='analysis-run-details-title' className='break-words text-base'>
          {run.name || title}
        </SheetTitle>
        <SheetDescription asChild>
          <div
            data-testid='analysis-run-details-status'
            data-status={run.status}
            className='flex items-center gap-1.5 text-sm [&>svg]:size-4'>
            <RunStatusIcon status={run.status} />
            {translateKey(t, `analysisLauncher.runs.status.${run.status}`)}
          </div>
        </SheetDescription>
        {run.status === ProcessJobStatus.Failed && run.error && (
          <ErrorBox error={run.error} testId='analysis-run-details-error' copyTestId='analysis-run-details-copy-error' />
        )}
      </SheetHeader>

      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4'>
        {mergeWarnings.length > 0 && (
          <section
            data-testid='analysis-run-details-warnings'
            aria-labelledby='analysis-run-details-warnings-title'
            className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-400'>
            <h3 id='analysis-run-details-warnings-title' className='font-semibold'>
              {t('landxmlMerge.warnings', { count: mergeWarnings.length })}
            </h3>
            <ul className='mt-2 list-disc space-y-1 break-words pl-5'>
              {mergeWarnings.map((warning, index) => <li key={index}>{warning}</li>)}
            </ul>
          </section>
        )}
        <dl data-testid='analysis-run-details-summary' className='grid grid-cols-2 gap-x-4 gap-y-3'>
          <Field label={t('analyses.details.startedBy')} testId='analysis-run-details-started-by'>
            {batch?.createdBy ? (
              <span className='flex items-center gap-1.5'>
                <img
                  src={batch.createdBy.avatar?.url || getPlaceholderAvatar(batch.createdBy.email || batch.createdBy.id)}
                  alt=''
                  className='size-4 rounded-full'
                />
                <span className='truncate'>{batch.createdBy.name}</span>
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label={t('analyses.details.started')} testId='analysis-run-details-started'>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className='cursor-help outline-none focus-visible:underline'>
                  {absolute(run.createdAt)}
                </span>
              </TooltipTrigger>
              <TooltipContent>{relative(run.createdAt)}</TooltipContent>
            </Tooltip>
          </Field>
          <Field label={t('analyses.details.finished')} testId='analysis-run-details-finished'>
            {run.finishedAt ? absolute(run.finishedAt) : t('analyses.details.notFinished')}
          </Field>
          <Field label={t('analyses.details.duration')} testId='analysis-run-details-duration'>
            {tookMs === null ? '—' : formatDuration(tookMs)}
          </Field>
          <Field label={t('analyses.details.batch')} testId='analysis-run-details-batch'>
            {run.batchId && batch?.key ? (
              <Link
                to={`${href}/datacenter?batch=${run.batchId}`}
                className='text-blue-700 underline-offset-2 hover:underline dark:text-blue-300'>
                {batch.key.toUpperCase()}
              </Link>
            ) : (
              '—'
            )}
          </Field>
          <Field label={t('analyses.details.totalSize')} testId='analysis-run-details-size'>
            {batch ? formatFileSize(batch.totalBytes) || '—' : '—'}
          </Field>
        </dl>

        <Section title={t('analyses.details.inputs')} testId='analysis-run-details-inputs'>
          <InputList layerIds={run.inputLayerIds} layersById={layersById} onOpenLayer={onOpenLayer} />
        </Section>

        <Section title={t('analyses.details.result')} testId='analysis-run-details-result'>
          {resultLayer ? (
            <div className='flex items-center justify-between gap-2'>
              <span className='truncate text-sm text-slate-900 dark:text-stone-100'>{resultLayer.name}</span>
              <Button
                type='button'
                size='sm'
                variant='outline'
                data-testid='analysis-run-details-open-result'
                onClick={() => onOpenLayer(resultLayer.id)}>
                <ExternalLink className='size-4' />
                <span className='ml-1'>{t('analyses.details.openInViewer')}</span>
              </Button>
            </div>
          ) : run.status === ProcessJobStatus.Processed ? (
            <p className={muted}>
              {allFiles.length > 0 ? t('analyses.details.downloadOnly') : t('analysisLauncher.runs.noResult')}
            </p>
          ) : (
            <p className={muted}>{t('analyses.details.noResultYet')}</p>
          )}
        </Section>

        <Section title={t('analyses.details.parameters')} testId='analysis-run-details-params'>
          {params.isPending ? (
            <Loader2 className='size-4 animate-spin text-slate-400' />
          ) : params.isError ? (
            <p className={muted}>{t('analyses.details.parametersFailed')}</p>
          ) : (
            <JobParamsPanel
              inputJson={params.data}
              type={run.type}
              staffTools={isStaff}
              collapseAfter={PARAMS_COLLAPSE_AFTER}
              emptyText={t('analyses.details.noParameters')}
            />
          )}
        </Section>

        <Section title={t('analyses.details.steps')} testId='analysis-run-details-steps'>
          {!batch ? (
            <Loader2 className='size-4 animate-spin text-slate-400' />
          ) : (
            <ul className='flex flex-col gap-1.5'>
              {steps.map((step) => (
                <StepLine key={step.id} step={step} isStaff={isStaff} />
              ))}
            </ul>
          )}
        </Section>

        <Section title={t('analyses.details.files')} testId='analysis-run-details-files'>
          <FileList files={allFiles} relative={relative} onDownload={onDownload} />
        </Section>
      </div>

      <SheetFooter className='flex-row flex-wrap justify-end border-t border-slate-200/70 dark:border-stone-800'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          data-testid='analysis-run-details-rerun'
          disabled={!canRerun}
          onClick={onRerun}>
          <RotateCw className='size-4' />
          <span className='ml-1'>{t('analyses.rerun')}</span>
        </Button>
        {run.batchId && (
          <Button asChild variant='outline' size='sm'>
            <Link data-testid='analysis-run-details-data-centre' to={`${href}/datacenter?batch=${run.batchId}`}>
              <Database className='size-4' />
              <span className='ml-1'>{t('analyses.details.openDataCentre')}</span>
            </Link>
          </Button>
        )}
        {run.batchId && canDelete && (
          <DeleteRunButton
            label={t('analyses.details.deleteRun')}
            isDeleting={isDeleting}
            onClick={() => run.batchId && confirmDelete(run.batchId, { active: isDeleteWarned(run) })}
          />
        )}
      </SheetFooter>
    </>
  );
}
